<template>
  <v-toolbar
    flat
    class="sc-top-bar"
    :class="{ 'sc-top-bar--mobile': $vuetify.breakpoint.xsOnly }"
    :extension-height="extensionHeight"
  >
    <v-app-bar-nav-icon v-if="$vuetify.breakpoint.xsOnly" @click="toggleMobileMenu" />
    <v-toolbar-title v-if="sourceType === 'dashboard'">
      <span>Welcome back, {{ getUserFullName }}</span>
    </v-toolbar-title>
    <v-toolbar-title v-else>
      <router-link
        v-if="showBackButton && currentPageName"
        class="pa-0 v-btn justify-start"
        :style="`font-size: ${$vuetify.breakpoint.xsOnly ? '10px' : '0.75rem' }`"
        color="primary"
        :to="getBackToListLinkParams()"
      >
        <span style="text-transform: capitalize;"> All {{ currentPageName }}</span>
      </router-link>
      <div :style="`font-size: ${$vuetify.breakpoint.xsOnly ? '16px' : '1.25rem' }`">
        {{ title }}
      </div>
    </v-toolbar-title>
    <v-spacer />
    <template v-if="$vuetify.breakpoint.smAndUp && $slots.extension">
      <slot name="extension" />
      <v-spacer />
    </template>
    <!--  IMPORTANT: v-else NOT works here  -->
    <template v-if="!$vuetify.breakpoint.smAndUp && $slots.extension" #extension>
      <slot name="extension" />
    </template>
    <template v-if="(allowEblast || allowSendTest) && $vuetify.breakpoint.smAndDown" slot="extension">
      <MarketingTopPanelSendButtons
        :allow-send="allowEblast"
        :allow-send-test="allowSendTest"
        :source-type="sourceType"
        :press-release-status="pressReleaseStatus"
        @sendEblastTest="sendEblastTest"
        @sendEblastCampaign="sendEblastCampaign"
      />
    </template>
    <MarketingTopPanelSendButtons
      v-if="(allowEblast || allowSendTest) && $vuetify.breakpoint.mdAndUp"
      :allow-send="allowEblast"
      :allow-send-test="allowSendTest"
      :source-type="sourceType"
      :press-release-status="pressReleaseStatus"
      @sendEblastTest="sendEblastTest"
      @sendEblastCampaign="sendEblastCampaign"
    />
    <v-chip v-if="$devmode" label class="dev-mode-bar px-2">
      {{ $vuetify.breakpoint.smAndUp ? 'Development Mode' : 'Dev' }}
    </v-chip>
    <FilterViewButton
      v-if="showFilterViewButton"
      :source="sourceType"
      value="dashboard"
      class="ml-2"
      current-view="dashboard"
      @eventHandler="$emit('filter-view:event', $event)"
    />
    <AiAgentButton />
    <v-divider
      v-if="$devmode"
      vertical
      :class="$vuetify.breakpoint.smAndUp ? 'ml-5 mr-3': 'mx-1'"
      style="opacity: .5"
      color="#f7f7f7"
    />
    <ProfileMenuWrapper />
  </v-toolbar>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import mixins from 'vue-typed-mixins';

import { appPanelName, sourceListRouteName } from '@/bundles/Panels/helpers';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { IGetAccountRouteHandler } from '@/bundles/App/helpers';
import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';

import ProfileMenuWrapper from '@/bundles/Panels/components/ProfileMenuWrapper.vue';
import MarketingTopPanelSendButtons from '@/bundles/Marketing/components/MarketingTopPanelSendButtons.vue';
import AiAgentButton from '@/bundles/AiAgent/components/AiAgentButton.vue';
import FilterViewButton from '@/components/common-components/app-filters/FilterViewButton.vue';

export default mixins(AccountLayoutMixin).extend({
  name: 'AppTopPanel',

  components: {
    MarketingTopPanelSendButtons,
    ProfileMenuWrapper,
    AiAgentButton,
    FilterViewButton,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    sourceType: {
      type: String,
      default: '',
    },
    allowEblast: Boolean,
    pressReleaseStatus: {
      type: String,
      default: ''
    },
    showBackButton: {
      type: Boolean,
      default: true
    },
    extensionHeight: {
      type: Number,
      default: 48
    },
    allowSendTest: Boolean,
    showFilterViewButton: Boolean,
  },

  computed: {
    ...mapGetters(StoreModulesNamesEnum.GroupFilters, {
      getFilterId: 'getFilterId',
      getLastSelectedType: 'getLastSelectedType'
    }),
    getUserFullName: function (): string {
      return this.$store.state.user?.name || this.$store.state.user?.profile?.first_name || '';
    },
    currentPageName: function (): string {
      return appPanelName[this.sourceType];
    },
  },

  created () {
    this.$eventBus.$on('go:back-to-list', this.goBackToList);
  },

  beforeDestroy () {
    this.$eventBus.$off('go:back-to-list', this.goBackToList);
  },

  methods: {
    sendEblastCampaign () {
      this.$emit('sendCampaign')
    },
    sendEblastTest () {
      this.$emit('sendTest')
    },
    toggleMobileMenu: function () {
      this.$store.state.nav = !this.$store.state.nav;
    },
    getBackToListLinkParams (): IGetAccountRouteHandler {
      const name = sourceListRouteName[this.sourceType];

      switch (this.getLastSelectedType(this.sourceType)) {
        case 'savedSet':
        case 'defaultFilter':
          const queryName = 'set';
          return this.getAccountRoute({
            name,
            query: { [queryName]: this.getFilterId(this.sourceType) }
          });
        case 'all':
          return this.getAccountRoute({
            name,
            query: { allRecords: 'true' }
          })
        default:
          return this.getAccountRoute({
            name,
          })
      }
    },
    goBackToList () {
      this.$router.push(this.getBackToListLinkParams());
    }
  }
});
</script>

<style scoped>
.v-toolbar__title:not(:first-child) {
  margin-left: 0 !important;
}
</style>
